import { useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Repeat2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { FactStatus } from "@/components/common";

// Repost a Fact (shared_fact_id) or a member's Fact post (shared_post_id) to your feed as-is; tap again to undo.
export const RepostButton = ({ item, setItem, field, testid }) => {
  const [busy, setBusy] = useState(false);
  const on = !!item.my_repost_id;
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) {
        await api.delete(`/posts/${item.my_repost_id}`);
        setItem((x) => ({ ...x, my_repost_id: null, share_count: Math.max(0, (x.share_count || 1) - 1) }));
        toast.success("Repost removed from your feed");
      } else {
        const { data } = await api.post("/posts", { [field]: item.id });
        setItem((x) => ({ ...x, my_repost_id: data.id, share_count: (x.share_count || 0) + 1 }));
        toast.success("Reposted to your feed. Your followers will see it");
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={toggle} disabled={busy} data-testid={testid} aria-pressed={on}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors hover:bg-black/5 disabled:opacity-60 ${on ? "font-medium text-emerald-700" : ""}`}>
      <Repeat2 size={17} strokeWidth={1.8} />{on ? "Reposted" : "Repost"}{item.share_count > 0 && <span className="text-slate-400">{item.share_count}</span>}
    </button>
  );
};

// A reposted Middle Live issue inside a feed post.
export const SharedLive = ({ e, testid }) => {
  if (!e) return <p className="mt-3 rounded-xl border border-dashed border-border p-3 text-sm text-slate-400" data-testid={testid}>This Live issue is no longer available.</p>;
  return (
    <Link to={`/live/${e.id}`} className="mt-3 block rounded-xl border border-border bg-[#FAF8F5] p-3.5 transition-colors hover:border-slate-300" data-testid={testid}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--facts))]"><Radio size={12} />Middle Live{e.category ? ` · ${e.category}` : ""}</p>
      <p className="mt-1.5 font-serif text-lg font-medium leading-snug text-slate-900">{e.title}</p>
      {e.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{e.summary}</p>}
    </Link>
  );
};

// A reposted Fact inside a feed post.
export const SharedFact = ({ f, testid }) => {
  if (!f) return <p className="mt-3 rounded-xl border border-dashed border-border p-3 text-sm text-slate-400" data-testid={testid}>This Fact is no longer available.</p>;
  const n = new Set((f.corroborating || []).map((c) => c.source_id)).size;
  return (
    <Link to={`/facts/${f.id}`} className="mt-3 block rounded-xl border border-border bg-[#FAF8F5] p-3.5 transition-colors hover:border-slate-300" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500"><FactStatus status={f.status} />{f.category && <span>{f.category}</span>}{f.published_at && <span>· {timeAgo(f.published_at)}</span>}</div>
      <p className="mt-2 font-serif text-lg font-medium leading-snug text-slate-900">{f.headline}</p>
      {f.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{f.summary}</p>}
      {f.status === "DISPUTED" && f.dispute_summary && <p className="mt-2 line-clamp-2 text-[13px] text-amber-900"><b>Sources disagree on:</b> {f.dispute_summary}</p>}
      <p className="mt-2 text-xs text-slate-500">{n ? `${n} approved source${n === 1 ? "" : "s"} back this up` : f.source_name}</p>
    </Link>
  );
};
